import React from 'react';
import {
  Box,
  Center,
  FormControl,
  FormErrorMessage,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { SchneiderHamIcon } from 'clipsal-cortex-icons/src/custom-icons';
import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';

import { ArcButton } from '../../../common/components/ArcButton';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { useOnboardingWizardProgress } from '../../home/useOnboardingWizardProgress';
import { selectSite } from '../siteSlice';
import {
  CreateSiteConsent,
  useCreateSiteConsentMutation,
  useGetSiteConsentByTypeQuery,
  useUpdateSiteConsentMutation,
} from './consentApi';

export type CustomerSupportConsentFormData = {
  hasPermission: 'yes' | 'no';
};

export function SetCustomerSupportConsent() {
  const { site_id: siteId } = useSelector(selectSite);
  const { t } = useTranslation();
  const schema = yup.object().shape({
    hasPermission: yup.string().nullable().required(t('Common.please select an option')),
  });
  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CustomerSupportConsentFormData>({
    resolver: yupResolver(schema),
  });
  const [createSiteConsent] = useCreateSiteConsentMutation();
  const [updateSiteConsent] = useUpdateSiteConsentMutation();
  const { data: customerSupportConsent, refetch } = useGetSiteConsentByTypeQuery('SHARE_WITH_CUSTOMER_CARE');
  const { needsToConfigureHardware, isLoaded } = useOnboardingWizardProgress();

  const toast = useToast({ isClosable: true });
  const iconColor = useColorModeValue('primaryBranding.600', 'primaryBranding.200');
  const navigate = useNavigate();
  const { isOpen: isAlertOpen, onOpen: onOpenAlert, onClose: onCloseAlert } = useDisclosure();

  const handleSubmitCustomerSupportConsent = async (values: CustomerSupportConsentFormData) => {
    if (values.hasPermission === 'no' && !isAlertOpen) {
      return onOpenAlert();
    }
    const body: CreateSiteConsent = {
      permission: values.hasPermission === 'yes',
    };
    try {
      if (customerSupportConsent) {
        await updateSiteConsent({ siteId, consentId: customerSupportConsent.id, body }).unwrap();
      } else {
        await createSiteConsent({ siteId, body }).unwrap();
      }

      await refetch().unwrap();

      if (needsToConfigureHardware) {
        const backURL = `../../set_customer_support_consent`;
        navigate(`../home/device-register?backURL=${backURL}`);
      } else {
        navigate(`../home`);
      }
    } catch (e) {
      console.error(e);
      toast({ title: t('Customer Support Consent.error saving'), status: 'error' });
    }
  };

  return (
    <SlidingAnimationPageBase
      px={5}
      includeTopNav
      includeTopNavHamButton
      title={t('Common.get started')}
      backURL={'/sites/list'}
    >
      <Center flexDirection="column">
        <SchneiderHamIcon w={'100px'} h={'100px'} color={iconColor} my={4} />

        <Text fontSize={21} fontWeight={600} textAlign="center" mb={5}>
          {t('Customer Support Consent.access to your')}
        </Text>
      </Center>
      <Text mb={4}>{t('Customer Support Consent.by granting access')}</Text>
      <Text mb={4}>{t('Customer Support Consent.if you decline')}</Text>
      <Text mb={2}>{t('Customer Support Consent.grant access?')}</Text>
      <Box
        onSubmit={handleFormSubmit(handleSubmitCustomerSupportConsent)}
        as={'form'}
        data-testid="customer-support-consent-form"
      >
        <FormControl mt={3} isInvalid={!!errors.hasPermission}>
          <RadioGroup>
            <Stack spacing={10} direction="row">
              <Radio value="yes" {...register('hasPermission')} data-testid="customer-support-consent-radio-input-yes">
                {t('Common.yes')}
              </Radio>
              <Radio value="no" {...register('hasPermission')} data-testid="customer-support-consent-radio-input-no">
                {t('Common.no')}
              </Radio>
            </Stack>
          </RadioGroup>
          <FormErrorMessage data-testid="customer-support-consent-input-error">
            {errors?.hasPermission?.message}
          </FormErrorMessage>
        </FormControl>

        <Center flexDirection="column">
          <ArcButton
            isLoading={isSubmitting}
            isDisabled={!isLoaded}
            my={5}
            type="submit"
            data-testid="save-customer-support-consent-button"
            w={'100%'}
            arcColor="#3DCD57"
          >
            {t('Common.continue')}
          </ArcButton>
        </Center>
        <AlertDialogModal
          isOpen={isAlertOpen}
          onClose={onCloseAlert}
          header={t('Common.are you sure?')}
          subHeader={t('Customer Support Consent.by opting out')}
          confirmButtonName={t('Common.continue')}
          onConfirm={handleFormSubmit(handleSubmitCustomerSupportConsent)}
          cancelButtonName={t('Common.cancel')}
        />
      </Box>
    </SlidingAnimationPageBase>
  );
}
